import { useState } from "react";
import { useNotifications } from "./NotificationContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck, Trash2, ChevronDown, ChevronRight, CheckCircle2, Info, AlertTriangle, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

const SEV_META: Record<string, { icon: any; ring: string; bg: string; text: string }> = {
  success: { icon: CheckCircle2,  ring: "ring-emerald-200 dark:ring-emerald-900", bg: "bg-emerald-50 dark:bg-emerald-950/40", text: "text-emerald-700 dark:text-emerald-300" },
  info:    { icon: Info,          ring: "ring-blue-200 dark:ring-blue-900",       bg: "bg-blue-50 dark:bg-blue-950/40",       text: "text-blue-700 dark:text-blue-300" },
  warning: { icon: AlertTriangle, ring: "ring-amber-200 dark:ring-amber-900",     bg: "bg-amber-50 dark:bg-amber-950/40",     text: "text-amber-700 dark:text-amber-300" },
  error:   { icon: XCircle,       ring: "ring-red-200 dark:ring-red-900",         bg: "bg-red-50 dark:bg-red-950/40",         text: "text-red-700 dark:text-red-300" },
};

export function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead, remove, clearAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const toggleExpanded = (id: string) => {
    setExpanded((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9" title="Avisos">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[460px] p-0 flex flex-col">
        <SheetHeader className="px-5 py-4 border-b">
          <div className="flex items-center justify-between gap-2">
            <div>
              <SheetTitle className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Avisos
              </SheetTitle>
              <SheetDescription>
                {notifications.length === 0
                  ? "Sin avisos pendientes"
                  : `${notifications.length} aviso${notifications.length === 1 ? "" : "s"} · ${unreadCount} sin leer`}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllRead} title="Marcar todo como leído">
                  <CheckCheck className="h-4 w-4" />
                </Button>
              )}
              {notifications.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setConfirmClearOpen(true)} title="Borrar todo">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-3">
                <Bell className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium">Sin avisos por ahora</p>
              <p className="text-xs text-muted-foreground mt-1">
                Aquí aparecerán los resultados de imports, sincronizaciones y procesos del sistema.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((n) => {
                const meta = SEV_META[n.severity];
                const Icon = meta.icon;
                const isExpanded = expanded.has(n.id);
                const hasItems = Array.isArray(n.items) && n.items.length > 0;
                return (
                  <div
                    key={n.id}
                    className={`px-5 py-3 hover:bg-muted/30 ${!n.read ? "bg-primary/[0.02]" : ""}`}
                    onClick={() => { if (!n.read) markRead(n.id); }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center ring-1 shrink-0 ${meta.bg} ${meta.text} ${meta.ring}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm leading-tight ${!n.read ? "font-semibold" : "font-medium"}`}>
                              {n.title}
                            </p>
                            {n.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">{n.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-[10px] text-muted-foreground">
                                hace {formatDistanceToNow(new Date(n.ts), { locale: es })}
                              </span>
                              {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 shrink-0"
                            onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                            title="Eliminar"
                          >
                            <Trash2 className="h-3 w-3 text-muted-foreground" />
                          </Button>
                        </div>

                        {hasItems && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(n.id); }}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                          >
                            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                            {n.items!.length} detalle{n.items!.length === 1 ? "" : "s"}
                          </button>
                        )}
                        {hasItems && isExpanded && (
                          <div className="mt-2 space-y-1 max-h-[200px] overflow-y-auto pr-1">
                            {n.items!.map((it, i) => (
                              <div key={i} className="text-[11px] bg-muted/40 rounded px-2 py-1">
                                <div className="font-mono">{it.label}</div>
                                {it.detail && <div className="text-muted-foreground mt-0.5">{it.detail}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </SheetContent>

      <ConfirmDialog
        open={confirmClearOpen}
        onOpenChange={setConfirmClearOpen}
        title="Borrar todos los avisos"
        description="¿Borrar todos los avisos? No se podrán recuperar."
        destructive
        confirmLabel="Borrar"
        onConfirm={() => { setConfirmClearOpen(false); clearAll(); }}
      />
    </Sheet>
  );
}
